import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import ConstructionContextSelector from '../../components/ConstructionContextSelector';
import ConstructionContextFilter from '../../components/ConstructionContextFilter';
import ConstructionBreadcrumb from '../../components/ConstructionBreadcrumb';
import ConstructionSearch from '../../components/ConstructionSearch';
import { matchesContextFilter } from '../../lib/constructionCache';
import { Upload as UploadIcon, FileText, Download, Loader2, File } from 'lucide-react';
import { toast } from 'sonner';
import { downloadFileUrl } from '../../lib/download';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Uploads = () => {
  const { projectId } = useParams();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [dataType, setDataType] = useState('scan');
  const [constructionCtx, setConstructionCtx] = useState({});
  const [filter, setFilter] = useState({});

  useEffect(() => {
    fetchUploads();
  }, [projectId]);

  const fetchUploads = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/uploads`);
      setUploads(response.data);
    } catch (error) {
      toast.error('Failed to fetch uploads');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dataType', dataType);
    if (constructionCtx && Object.keys(constructionCtx).length > 0) {
      formData.append('constructionContext', JSON.stringify(constructionCtx));
    }

    try {
      await axios.post(`${API}/projects/${projectId}/uploads`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('File uploaded successfully');
      setFile(null);
      fetchUploads();
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadFile = async (uploadId, fileName) => {
    try {
      const response = await axios.get(`${API}/uploads/${uploadId}/signed-url`);
      await downloadFileUrl(response.data.url, fileName);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Project Uploads</h1>
          <p className="text-slate-600 mt-1">Upload and manage project files</p>
        </div>

        <ConstructionSearch projectId={projectId} onSelect={(item, group) => toast.info(`${group}: ${item.name || item.fileName}`)} />

        <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Upload New File</h2>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Data Type</label>
              <select value={dataType} onChange={(e) => setDataType(e.target.value)} className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" data-testid="data-type-select">
                <option value="scan">Scan</option>
                <option value="object_count">Object Count</option>
                <option value="measurement">Measurement</option>
                <option value="other">Other</option>
              </select>
            </div>
            <ConstructionContextSelector projectId={projectId} value={constructionCtx} onChange={setConstructionCtx} />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">File</label>
              <input type="file" onChange={(e) => setFile(e.target.files[0])} className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" required data-testid="file-input" />
            </div>
            <button type="submit" disabled={uploading || !file} className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 shadow-md" data-testid="upload-button">
              {uploading ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Uploading...</span></> : <><UploadIcon className="w-5 h-5" /><span>Upload File</span></>}
            </button>
          </form>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-lg font-semibold text-slate-900">Uploaded Files</h2>
            <ConstructionContextFilter projectId={projectId} value={filter} onChange={setFilter} testId="uploads-context-filter" />
          </div>
          <div className="divide-y divide-slate-200">
            {uploads.filter((u) => matchesContextFilter(u.constructionContext, filter)).map((upload) => (
              <div key={upload.id} className="px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors gap-4" data-testid="upload-item">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                    <File className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 truncate">{upload.fileName}</p>
                    <p className="text-sm text-slate-500">{upload.dataType} • {(upload.size / 1024).toFixed(2)} KB</p>
                    <div className="mt-1"><ConstructionBreadcrumb projectId={projectId} context={upload.constructionContext} fallback="Unassigned location" testId={`upload-ctx-${upload.id}`} /></div>
                  </div>
                </div>
                <button onClick={() => downloadFile(upload.id, upload.fileName)} className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors shrink-0" data-testid="download-button">
                  <Download className="w-4 h-4" />
                  <span>View</span>
                </button>
              </div>
            ))}
          </div>
          {uploads.length === 0 && (
            <div className="text-center py-12 text-slate-500">No uploads yet. Upload your first file above.</div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Uploads;
